import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Link from 'next/link';
import { useRouter } from 'next/router';
import moment from 'moment';
import colors from '../../constants/colors';
import sortPrograms from '../../utils/sortPrograms';
import api from '../../utils/api';

const MILESTONES = [1, 2, 3, 5, 10, 20];
const CIRCUMFERENCE = 2 * Math.PI * 52; // ~326.7

// --- Styled Components (Full State) ---

const WidgetContainer = styled.div``;

const RingLayout = styled.div`
  display: flex;
  align-items: center;
  gap: 28px;

  @media (max-width: 480px) {
    flex-direction: column;
    text-align: center;
  }
`;

const RingCircleWrap = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  flex-shrink: 0;
`;

const RingSvg = styled.svg`
  width: 120px;
  height: 120px;
  transform: rotate(-90deg);
`;

const RingTrack = styled.circle`
  fill: none;
  stroke: ${colors.background.maincolor};
  stroke-width: 6;
`;

const RingProgress = styled.circle`
  fill: none;
  stroke: url(#streak-ring-gradient);
  stroke-width: 6;
  stroke-linecap: round;
  transition: stroke-dashoffset 1s ease;
`;

const RingCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
`;

const RingNum = styled.div`
  font-size: 36px;
  font-weight: bold;
  color: ${(props) =>
    props.$dimmed ? colors.main.placeholdersAndDisabledBtnsText : colors.main.black};
  line-height: 1;
  letter-spacing: -1px;
`;

const RingUnit = styled.div`
  font-size: 10px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: ${colors.main.grey};
  margin-top: 2px;
`;

const InfoPanel = styled.div`
  flex: 1;
  min-width: 0;
`;

const NextLabel = styled.div`
  font-size: 11px;
  color: ${colors.main.grey};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 4px;
`;

const NextMilestone = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: ${colors.main.black};
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 1px;

  span {
    color: ${colors.main.ruby};
  }
`;

const Badges = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 16px;

  @media (max-width: 480px) {
    justify-content: center;
  }
`;

const Badge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
  transition: all 0.2s;

  ${(props) => {
    if (props.$status === 'earned') {
      return `
        background: ${colors.main.blue};
        color: white;
        box-shadow: 0 2px 8px rgba(14,160,195,0.2);
      `;
    }
    if (props.$status === 'next') {
      return `
        background: white;
        border: 2px dashed ${colors.main.blue};
        color: ${colors.main.blue};
      `;
    }
    return `
      background: ${colors.background.maincolor};
      color: ${colors.main.grey};
    `;
  }}
`;

const Stats = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: 480px) {
    justify-content: center;
  }
`;

const StatVal = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: ${colors.main.black};
`;

const StatLabel = styled.div`
  font-size: 10px;
  color: ${colors.main.grey};
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const CtaBanner = styled.a`
  margin-top: 20px;
  padding: 12px 16px;
  background: rgba(14, 160, 195, 0.04);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s;

  &:hover {
    background: rgba(14, 160, 195, 0.08);
  }

  &:focus-visible {
    outline: 2px solid ${colors.main.blue};
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    text-align: center;
  }
`;

const CtaMsg = styled.div`
  font-size: 13px;
  color: ${colors.main.black};

  span {
    color: ${colors.main.blue};
    font-weight: bold;
  }
`;

const DaysPill = styled.div`
  font-size: 12px;
  font-weight: bold;
  color: ${colors.main.blue};
  background: rgba(14, 160, 195, 0.08);
  padding: 4px 10px;
  border-radius: 20px;
  white-space: nowrap;
`;

// --- Styled Components (Compact State) ---

const CompactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
    text-align: center;
  }
`;

const CompactCheck = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${colors.main.blue};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;
  font-size: 18px;
`;

const CompactInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CompactTitle = styled.div`
  font-size: 15px;
  font-weight: bold;
  color: ${colors.main.black};

  span {
    color: ${colors.main.ruby};
  }
`;

const CompactSub = styled.div`
  font-size: 13px;
  color: ${colors.main.grey};
  margin-top: 2px;

  span {
    color: ${colors.main.blue};
    font-weight: bold;
  }
`;

const CompactBadges = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
`;

const CompactBadge = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;

  ${(props) => {
    if (props.$status === 'earned') {
      return `
        background: ${colors.main.blue};
        color: white;
      `;
    }
    if (props.$status === 'next') {
      return `
        border: 2px dashed ${colors.main.blue};
        color: ${colors.main.blue};
      `;
    }
    return `
      background: ${colors.background.maincolor};
      color: ${colors.main.grey};
    `;
  }}
`;

// --- Helpers ---

const getNextMilestone = (currentStreak) => {
  const next = MILESTONES.find((m) => m > currentStreak);
  return next || MILESTONES[MILESTONES.length - 1];
};

const getPrevMilestone = (currentStreak) => {
  const passed = MILESTONES.filter((m) => m <= currentStreak);
  return passed.length ? passed[passed.length - 1] : 0;
};

const getProgressFraction = (currentStreak) => {
  const next = getNextMilestone(currentStreak);
  const prev = getPrevMilestone(currentStreak);
  if (currentStreak >= MILESTONES[MILESTONES.length - 1]) return 1;
  if (next === prev) return 1;
  return (currentStreak - prev) / (next - prev);
};

const getBadgeStatus = (milestone, currentStreak) => {
  if (milestone <= currentStreak) return 'earned';
  const next = getNextMilestone(currentStreak);
  if (milestone === next) return 'next';
  return 'locked';
};

const BADGE_STATUS_LABEL = {
  earned: 'earned',
  next: 'next milestone',
  locked: 'locked',
};

const getBadgeLabel = (milestone, status) =>
  `${milestone} week streak — ${BADGE_STATUS_LABEL[status]}`;

const getDaysLeftInWeek = () => {
  const endOfWeek = moment().endOf('isoWeek');
  return Math.ceil(endOfWeek.diff(moment(), 'days', true));
};

const MILESTONE_EMOJI = {
  1: '☝🏽',
  2: '✌🏽',
  3: '🤟🏽',
  5: '🖐🏽',
  10: '🙌🏽',
  20: '🏆',
};

// --- Component ---

const StreakWidget = () => {
  const router = useRouter();
  const achievements = useSelector((state) => state.achievements?.all || []);
  const programs = useSelector((state) => state.programs);

  const [streakData, setStreakData] = useState(null);

  const fetchStreak = useCallback(() => {
    api()
      .get('/users/streak')
      .then(({ data }) => setStreakData(data))
      .catch(() => {});
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchStreak();
  }, [fetchStreak]);

  // Refetch when navigating back to dashboard (e.g. after completing a workout)
  useEffect(() => {
    router.events.on('routeChangeComplete', fetchStreak);
    return () => {
      router.events.off('routeChangeComplete', fetchStreak);
    };
  }, [router.events, fetchStreak]);

  const highestStreak = useMemo(() => {
    const ACHIEVEMENT_TYPE_WEEK = 2;
    const streakAchievements = achievements.filter(
      (a) => a.type === ACHIEVEMENT_TYPE_WEEK && a.earnedAt,
    );
    return streakAchievements.reduce((max, a) => {
      return a.timesValue > max ? a.timesValue : max;
    }, 0);
  }, [achievements]);

  // Don't render until data has loaded
  if (!streakData) {
    return null;
  }

  const weekStreak = streakData.currentStreak;
  const totalSessions = streakData.totalCompletedSessions;
  const doneThisWeek = streakData.doneThisWeek;
  const nextMilestone = getNextMilestone(weekStreak);
  const maxReached = weekStreak >= MILESTONES[MILESTONES.length - 1];

  // --- Compact state: session done this week, streak is safe ---
  if (doneThisWeek && weekStreak > 0) {
    const weeksToGo = maxReached ? 0 : nextMilestone - weekStreak;
    const emoji = MILESTONE_EMOJI[weekStreak] || MILESTONE_EMOJI[nextMilestone] || '';

    return (
      <WidgetContainer>
        <CompactRow>
          <CompactCheck>✓</CompactCheck>
          <CompactInfo>
            <CompactTitle>
              {emoji} <span>{weekStreak}</span> Week Streak
            </CompactTitle>
            <CompactSub>
              {maxReached ? (
                <>🏆 Max streak reached — keep it going!</>
              ) : weeksToGo === 1 ? (
                <>
                  Train next week → <span>{nextMilestone} week badge</span>
                </>
              ) : (
                <>
                  Keep training → <span>{nextMilestone} week badge</span> in {weeksToGo} weeks
                </>
              )}
            </CompactSub>
          </CompactInfo>
          <CompactBadges>
            {MILESTONES.map((m) => {
              const status = getBadgeStatus(m, weekStreak);
              return (
                <CompactBadge key={m} $status={status} title={getBadgeLabel(m, status)}>
                  {m}
                </CompactBadge>
              );
            })}
          </CompactBadges>
        </CompactRow>
      </WidgetContainer>
    );
  }

  // --- Full state: no session yet this week, need to motivate ---
  const featuredProgram = programs?.total ? sortPrograms([...programs.all])[0] : null;
  const sessionUrl =
    featuredProgram?.nextSession && featuredProgram?.id
      ? `/programs/${featuredProgram.id}/sessions/${featuredProgram.nextSession.id}/start?trackSessionId=${featuredProgram.nextTrackSessionId}`
      : null;

  const progress = getProgressFraction(weekStreak);
  const dashOffset = CIRCUMFERENCE * (1 - progress);
  const daysLeft = getDaysLeftInWeek();

  let ctaMessage;
  let nextLabelText;
  let nextMilestoneText;

  if (weekStreak === 0) {
    nextLabelText = 'First Milestone';
    nextMilestoneText = '1';
    ctaMessage = (
      <CtaMsg>
        Complete <span>1 session</span> to start your streak
      </CtaMsg>
    );
  } else {
    nextLabelText = 'Keep Your Streak Alive';
    nextMilestoneText = String(nextMilestone);
    const weeksToGo = nextMilestone - weekStreak;
    ctaMessage = (
      <CtaMsg>
        Complete a session to lock in week {weekStreak + 1} —{' '}
        <span>
          {weeksToGo} more{weeksToGo !== 1 ? ' weeks' : ' week'}
        </span>{' '}
        to your next badge
      </CtaMsg>
    );
  }

  return (
    <WidgetContainer>
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <linearGradient id="streak-ring-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={colors.main.blue} />
            <stop offset="100%" stopColor={colors.main.lightBlue} />
          </linearGradient>
        </defs>
      </svg>

      <RingLayout>
        <RingCircleWrap>
          <RingSvg viewBox="0 0 120 120">
            <RingTrack cx="60" cy="60" r="52" />
            <RingProgress
              cx="60"
              cy="60"
              r="52"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
            />
          </RingSvg>
          <RingCenter>
            <RingNum $dimmed={weekStreak === 0}>{weekStreak}</RingNum>
            <RingUnit>week{weekStreak !== 1 ? 's' : ''}</RingUnit>
          </RingCenter>
        </RingCircleWrap>

        <InfoPanel>
          <NextLabel>{nextLabelText}</NextLabel>
          <NextMilestone>
            {MILESTONE_EMOJI[nextMilestoneText] && <>{MILESTONE_EMOJI[nextMilestoneText]} </>}
            <span>{nextMilestoneText}</span> Week Streak
          </NextMilestone>

          <Badges>
            {MILESTONES.map((m) => {
              const status = getBadgeStatus(m, weekStreak);
              return (
                <Badge key={m} $status={status} title={getBadgeLabel(m, status)}>
                  {m}
                </Badge>
              );
            })}
          </Badges>

          <Stats>
            {highestStreak > 0 && (
              <div>
                <StatVal>{highestStreak}</StatVal>
                <StatLabel>Best Streak</StatLabel>
              </div>
            )}
            <div>
              <StatVal>{totalSessions}</StatVal>
              <StatLabel>Sessions</StatLabel>
            </div>
          </Stats>
        </InfoPanel>
      </RingLayout>

      {sessionUrl ? (
        <Link href={sessionUrl} passHref legacyBehavior>
          <CtaBanner>
            {ctaMessage}
            <DaysPill>
              {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
            </DaysPill>
          </CtaBanner>
        </Link>
      ) : (
        <CtaBanner as="div" style={{ cursor: 'default' }}>
          {ctaMessage}
          <DaysPill>
            {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
          </DaysPill>
        </CtaBanner>
      )}
    </WidgetContainer>
  );
};

export default StreakWidget;
